<script setup lang="ts">
import { useDowntimes } from '@/composables/eos/downtime'
import type { IDowntimeCreationForm } from '@/types/eos/forms'
import { DateTime } from 'luxon'
import { computed, useTemplateRef } from 'vue'
import DowntimeForm from './DowntimeForm.vue'
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'

const props = defineProps<{
  startDatetime?: string
  endDatetime?: string
  tz?: string
  site?: ISiteIdentifier
}>()

const emit = defineEmits(['create'])

const visible = defineModel<boolean>()
const formRef = useTemplateRef('formRef')

const { createDowntimeMutation } = useDowntimes()
const { mutate: createDowntime, isPending: isCreatePending, error: createError } = createDowntimeMutation

const start = computed<DateTime | undefined>(() =>
  props.startDatetime ? DateTime.fromISO(props.startDatetime, { zone: props.tz }) : undefined
)

const startDTNoOffset = computed<string | undefined>(() => start.value?.toFormat("yyyy-MM-dd'T'HH:mm:ss"))
const endDTNoOffset = computed<string | undefined>(() => {
  const endDT = props.endDatetime
    ? DateTime.fromISO(props.endDatetime, { zone: props.tz })
    : start.value?.plus({ hour: 1 })
  return endDT?.toFormat("yyyy-MM-dd'T'HH:mm:ss")
})

const tzOverride = computed<string | undefined>(() => (props.tz !== props.site?.timezone ? props.tz : undefined))

const handleOpen = async () => {
  await formRef.value?.init()
}

const handleClosed = () => {
  formRef.value?.resetFields()
  createDowntimeMutation.reset()
}

const handleSave = () => {
  formRef.value?.submit()
}

const handleCreate = (form: IDowntimeCreationForm) => {
  createDowntime(form, {
    onSuccess: () => {
      visible.value = false
      emit('create')
    },
  })
}
</script>

<template>
  <el-dialog
    v-model="visible"
    title="Create Downtime"
    :width="784"
    :show-close="false"
    :close-on-click-modal="false"
    @open="handleOpen"
    @closed="handleClosed"
  >
    <DowntimeForm
      ref="formRef"
      :start-datetime="startDTNoOffset"
      :end-datetime="endDTNoOffset"
      :site="site"
      :tz-override="tzOverride"
      :disabled="isCreatePending"
      @create="handleCreate"
    />
    <RlErrorAlert v-if="createError" :error="createError" />
    <template #footer>
      <el-button @click="visible = false">Cancel</el-button>
      <el-button type="primary" :loading="isCreatePending" @click="handleSave">Save</el-button>
    </template>
  </el-dialog>
</template>
